import React from "react";
import {
  Eye,
  Pencil,
  Trash2,
  Send,
  Archive,
  RotateCcw,
  Power,
  BarChart3,
} from "lucide-react";

const statusStyles = {
  draft: "bg-gray-100 text-gray-700 ring-gray-200",
  published:
    "bg-emerald-100 text-emerald-700 ring-emerald-200",
  archived:
    "bg-amber-100 text-amber-700 ring-amber-200",
  active:
    "bg-blue-100 text-blue-700 ring-blue-200",
  inactive:
    "bg-red-100 text-red-700 ring-red-200",
};

const normalizeStatus = (status) =>
  String(status || "draft").toLowerCase();

const formatDate = (date) => {
  if (!date) return "—";

  const parsedDate = new Date(date);

  if (Number.isNaN(parsedDate.getTime())) {
    return "—";
  }

  return parsedDate.toLocaleDateString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

const getQuizId = (quiz) => quiz?._id || quiz?.id;

const getSubjectName = (quiz) => {
  if (!quiz?.questionBankSubject) {
    return "—";
  }

  if (typeof quiz.questionBankSubject === "object") {
    return (
      quiz.questionBankSubject.name ||
      quiz.questionBankSubject.title ||
      quiz.questionBankSubject._id ||
      "—"
    );
  }

  return quiz.questionBankSubject;
};

const IconButton = ({
  title,
  onClick,
  children,
  className = "",
  disabled = false,
}) => (
  <button
    type="button"
    title={title}
    aria-label={title}
    onClick={onClick}
    disabled={disabled}
    className={`inline-flex h-8 w-8 items-center justify-center rounded-lg
      border border-gray-200 bg-white text-gray-600
      transition hover:border-gray-300 hover:bg-gray-50
      hover:text-gray-900 disabled:cursor-not-allowed
      disabled:opacity-40 ${className}`}
  >
    {children}
  </button>
);

const EnglishQuizTable = ({
  quizzes = [],
  isLoading = false,
  error = null,

  onView,
  onEdit,
  onDelete,

  onPublish,
  onUnpublish,
  onArchive,
  onActivate,

  onStatistics,

  deletingQuizId = null,
  publishingQuizId = null,
  unpublishingQuizId = null,
  archivingQuizId = null,
  activatingQuizId = null,
}) => {
  if (isLoading) {
    return (
      <div className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm">
        <div className="animate-pulse">
          <div className="h-12 bg-gray-100" />

          {Array.from({ length: 6 }).map((_, index) => (
            <div
              key={index}
              className="grid grid-cols-7 gap-4 border-t border-gray-100 px-6 py-4"
            >
              {Array.from({ length: 7 }).map(
                (_, cellIndex) => (
                  <div
                    key={cellIndex}
                    className="h-4 rounded bg-gray-200"
                  />
                ),
              )}
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (error) {
    const message =
      error?.data?.message ||
      error?.error ||
      "Unable to load English quizzes.";

    return (
      <div className="rounded-xl border border-red-200 bg-red-50 p-5 text-sm text-red-700">
        <p className="font-semibold">
          Failed to load quizzes
        </p>

        <p className="mt-1">{message}</p>
      </div>
    );
  }

  if (!quizzes.length) {
    return (
      <div className="rounded-xl border border-gray-200 bg-white p-10 text-center shadow-sm">
        <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-blue-50 text-blue-600">
          <BarChart3 size={22} />
        </div>

        <h3 className="mt-4 text-base font-semibold text-gray-900">
          No English quizzes found
        </h3>

        <p className="mt-1 text-sm text-gray-500">
          Create an English quiz to see it listed here.
        </p>
      </div>
    );
  }

  return (
    <div className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm">
      <div className="overflow-x-auto">
        <table className="min-w-[1050px] w-full border-collapse">
          <thead>
            <tr className="border-b border-gray-200 bg-gray-50">
              {/* Quiz */}
              <th className="px-5 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">
                Quiz
              </th>

              {/* Subject */}
              <th className="px-5 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">
                Subject
              </th>

              {/* Duration */}
              <th className="px-5 py-3 text-center text-xs font-semibold uppercase tracking-wide text-gray-500">
                Duration
              </th>

              {/* Passing */}
              <th className="px-5 py-3 text-center text-xs font-semibold uppercase tracking-wide text-gray-500">
                Passing
              </th>

              {/* Status */}
              <th className="px-5 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">
                Status
              </th>

              {/* Created */}
              <th className="px-5 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">
                Created
              </th>

              {/* Actions */}
              <th className="sticky right-0 z-10 bg-gray-50 px-5 py-3 text-right text-xs font-semibold uppercase tracking-wide text-gray-500">
                Actions
              </th>
            </tr>
          </thead>

          <tbody className="divide-y divide-gray-100">
            {quizzes.map((quiz) => {
              const quizId = getQuizId(quiz);

              const status = normalizeStatus(
                quiz.status,
              );

              const isPublished =
                status === "published";

              const isArchived =
                status === "archived";

              const isActive =
                quiz.isActive !== false;

              const isDeleting =
                deletingQuizId === quizId;

              const isPublishing =
                publishingQuizId === quizId;

              const isUnpublishing =
                unpublishingQuizId === quizId;

              const isArchiving =
                archivingQuizId === quizId;

              const isActivating =
                activatingQuizId === quizId;

              return (
                <tr
                  key={quizId}
                  className="group transition hover:bg-gray-50"
                >
                  {/* Quiz */}
                  <td className="px-5 py-4">
                    <div className="min-w-[260px]">
                      <button
                        type="button"
                        onClick={() =>
                          onView?.(quiz)
                        }
                        className="text-left"
                      >
                        <p className="font-semibold text-gray-900 transition hover:text-blue-600">
                          {quiz.name ||
                            "Untitled English Quiz"}
                        </p>
                      </button>

                      {quiz.description && (
                        <p className="mt-1 max-w-[320px] truncate text-xs text-gray-500">
                          {quiz.description}
                        </p>
                      )}
                    </div>
                  </td>

                  {/* Subject */}
                  <td className="px-5 py-4">
                    <span className="text-sm text-gray-700">
                      {getSubjectName(quiz)}
                    </span>
                  </td>

                  {/* Duration */}
                  <td className="px-5 py-4 text-center">
                    <span className="text-sm text-gray-700">
                      {quiz.durationMinutes
                        ? `${quiz.durationMinutes} min`
                        : "—"}
                    </span>
                  </td>

                  {/* Passing */}
                  <td className="px-5 py-4 text-center">
                    <span className="text-sm text-gray-700">
                      {quiz.passingPercentage !==
                      undefined
                        ? `${quiz.passingPercentage}%`
                        : "—"}
                    </span>
                  </td>

                  {/* Status */}
                  <td className="px-5 py-4">
                    <div className="flex flex-col items-start gap-1">
                      <span
                        className={`inline-flex items-center rounded-full px-2.5 py-1 text-xs font-semibold ring-1 ring-inset ${
                          statusStyles[status] ||
                          statusStyles.draft
                        }`}
                      >
                        <span className="mr-1.5 h-1.5 w-1.5 rounded-full bg-current" />

                        {status
                          .charAt(0)
                          .toUpperCase() +
                          status.slice(1)}
                      </span>

                      {!isActive &&
                        !isArchived && (
                          <span className="text-[11px] text-gray-400">
                            Inactive
                          </span>
                        )}
                    </div>
                  </td>

                  {/* Created */}
                  <td className="whitespace-nowrap px-5 py-4">
                    <span className="text-sm text-gray-600">
                      {formatDate(
                        quiz.createdAt,
                      )}
                    </span>
                  </td>

                  {/* Actions */}
                  <td className="sticky right-0 bg-white px-5 py-4 group-hover:bg-gray-50">
                    <div className="flex items-center justify-end gap-1.5">
                      {/* View */}
                      {onView && (
                        <IconButton
                          title="View quiz"
                          onClick={() =>
                            onView(quiz)
                          }
                        >
                          <Eye size={16} />
                        </IconButton>
                      )}

                      {/* Edit */}
                      {onEdit && !isPublished && (
                        <IconButton
                          title="Edit quiz"
                          onClick={() =>
                            onEdit(quiz)
                          }
                        >
                          <Pencil size={16} />
                        </IconButton>
                      )}

                      {/* Statistics */}
                      {onStatistics && (
                        <IconButton
                          title="View statistics"
                          onClick={() =>
                            onStatistics(quiz)
                          }
                          className="text-purple-600 hover:text-purple-700"
                        >
                          <BarChart3
                            size={16}
                          />
                        </IconButton>
                      )}

                      {/* Publish */}
                      {onPublish &&
                        status === "draft" && (
                          <IconButton
                            title="Publish quiz"
                            onClick={() =>
                              onPublish(quiz)
                            }
                            disabled={
                              isPublishing
                            }
                            className="text-emerald-600 hover:text-emerald-700"
                          >
                            <Send size={16} />
                          </IconButton>
                        )}

                      {/* Unpublish */}
                      {onUnpublish &&
                        isPublished && (
                          <IconButton
                            title="Unpublish quiz"
                            onClick={() =>
                              onUnpublish(quiz)
                            }
                            disabled={
                              isUnpublishing
                            }
                            className="text-orange-600 hover:text-orange-700"
                          >
                            <RotateCcw
                              size={16}
                            />
                          </IconButton>
                        )}

                      {/* Archive */}
                      {onArchive &&
                        !isArchived && (
                          <IconButton
                            title="Archive quiz"
                            onClick={() =>
                              onArchive(quiz)
                            }
                            disabled={
                              isArchiving
                            }
                            className="text-amber-600 hover:text-amber-700"
                          >
                            <Archive
                              size={16}
                            />
                          </IconButton>
                        )}

                      {/* Activate */}
                      {onActivate &&
                        isArchived && (
                          <IconButton
                            title="Activate quiz"
                            onClick={() =>
                              onActivate(quiz)
                            }
                            disabled={
                              isActivating
                            }
                            className="text-blue-600 hover:text-blue-700"
                          >
                            <Power size={16} />
                          </IconButton>
                        )}

                      {/* Delete */}
                      {onDelete && (
                        <IconButton
                          title="Delete quiz"
                          onClick={() =>
                            onDelete(quiz)
                          }
                          disabled={
                            isDeleting ||
                            isPublished
                          }
                          className="text-red-600 hover:border-red-200 hover:bg-red-50 hover:text-red-700"
                        >
                          <Trash2 size={16} />
                        </IconButton>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <div className="flex items-center justify-between border-t border-gray-100 bg-gray-50 px-5 py-3">
        <p className="text-xs text-gray-500">
          Showing{" "}
          <span className="font-semibold text-gray-700">
            {quizzes.length}
          </span>{" "}
          {quizzes.length === 1
            ? "quiz"
            : "quizzes"}
        </p>

        <p className="text-xs text-gray-400">
          Scroll horizontally to view all columns
        </p>
      </div>
    </div>
  );
};

export default EnglishQuizTable;